import {
  hexToRgb,
  rgbToHex,
  rgbToCmyk,
  simulatePrint,
  simulateHomeLaser,
  formatRgb,
  formatCmyk,
  textOn,
  printFidelity,
} from "../lib/color";
import { CopyButton } from "./CopyButton";

function DataRow({ label, value, textColor }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span
        style={{ color: textColor }}
        className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-80"
      >
        {label}
      </span>
      <span style={{ color: textColor }} className="font-mono text-xs md:text-sm">
        {value}
      </span>
    </div>
  );
}

function SwatchPanel({ testId, bg, topLabel, txt, rows, borderRight }) {
  return (
    <div
      data-testid={testId}
      className={`relative h-64 md:h-80 p-5 flex flex-col justify-between transition-colors duration-150 ${
        borderRight ? "border-r border-border" : ""
      }`}
      style={{ backgroundColor: bg }}
    >
      <div className="flex justify-between items-start">
        <span
          className="text-[10px] font-medium uppercase tracking-[0.2em]"
          style={{ color: txt }}
        >
          {topLabel}
        </span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <DataRow key={r.label} label={r.label} value={r.value} textColor={txt} />
        ))}
      </div>
    </div>
  );
}

export function SwatchPreview({ hex }) {
  const rgb = hexToRgb(hex);
  const cmyk = rgbToCmyk(rgb);
  const printRgb = simulatePrint(rgb);
  const printHex = rgbToHex(printRgb);
  const laserRgb = simulateHomeLaser(rgb);
  const laserHex = rgbToHex(laserRgb);
  const txtScreen = textOn(rgb);
  const txtPrint = textOn(printRgb);
  const txtLaser = textOn(laserRgb);
  const fidelity = printFidelity(rgb);

  return (
    <div className="border border-border bg-surface">
      <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-border">
        <div className="border-b sm:border-b-0 sm:border-r border-border px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted">
            Screen · RGB
          </span>
          <CopyButton testId="copy-hex-screen" value={hex} />
        </div>
        <div className="border-b sm:border-b-0 sm:border-r border-border px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted">
            Print · CMYK Simulated
          </span>
          <span className="font-mono text-[10px] text-muted">
            FOGRA39 · Fidelity {fidelity}
          </span>
        </div>
        <div className="px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted">
            Home Laser Printer
          </span>
          <span className="font-mono text-[10px] text-muted">
            Consumer toner
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3">
        <SwatchPanel
          testId="swatch-preview-rgb"
          bg={hex}
          topLabel="On-screen"
          txt={txtScreen}
          borderRight
          rows={[
            { label: "HEX", value: hex.toUpperCase() },
            { label: "RGB", value: formatRgb(rgb) },
          ]}
        />
        <SwatchPanel
          testId="swatch-preview-cmyk"
          bg={printHex}
          topLabel="Printed (Sim.)"
          txt={txtPrint}
          borderRight
          rows={[
            { label: "CMYK", value: formatCmyk(cmyk) },
            { label: "HEX·SIM", value: printHex.toUpperCase() },
          ]}
        />
        <SwatchPanel
          testId="swatch-preview-laser"
          bg={laserHex}
          topLabel="Home Laser"
          txt={txtLaser}
          rows={[
            { label: "HEX·LASER", value: laserHex.toUpperCase() },
            { label: "RGB", value: formatRgb(laserRgb) },
          ]}
        />
      </div>
    </div>
  );
}
